/** Generische CRUD-Basis — Pendant zu `Services/Base/APIBase.cs`. */

import { apiClient } from './httpClient';
import { getCurrentAppUri } from './requestContext';

export interface CrudApi<T> {
  readonly endpoint: string;
  get(): Promise<T[]>;
  getEntry(id: number | string): Promise<T>;
  create(model: T): Promise<T>;
  put(id: number | string, model: T): Promise<T>;
  remove(id: number): Promise<T>;
}

/** Pendant zu `APIBase.GetUri` inklusive Endpunkt. */
export function buildAppPath(endpoint: string, version: 'v1' = 'v1'): string {
  return `${version}/${getCurrentAppUri()}/${endpoint}`;
}

/**
 * `APIBase` liefert im Fehlerfall `null`, obwohl die Signatur das nicht vorsieht
 * (`catch { return null; }`). Die Aufrufer prüfen — wie im Altclient — selbst auf ein
 * leeres Ergebnis, deshalb bleibt der Rückgabetyp bewusst nicht nullable.
 */
export function nullResult<T>(): T {
  return null as unknown as T;
}

/** Führt die Anfrage aus und liefert bei jedem Fehler das Ersatzergebnis. */
export async function requestOrFallback<T>(request: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await request();
  } catch {
    return fallback;
  }
}

/** Liefert `response.ok`; jeder Fehler ergibt `false`. */
export async function requestSucceeded(request: () => Promise<Response>): Promise<boolean> {
  return requestOrFallback(async () => (await request()).ok, false);
}

export function createCrudApi<T>(endpoint: string): CrudApi<T> {
  const path = (): string => buildAppPath(endpoint);

  return {
    endpoint,
    get: () => requestOrFallback(() => apiClient.get<T[]>(path()), []),
    getEntry: (id) => requestOrFallback(() => apiClient.get<T>(`${path()}/${id}`), nullResult<T>()),
    create: (model) => requestOrFallback(() => apiClient.post<T>(path(), model), nullResult<T>()),
    put: (id, model) =>
      requestOrFallback(() => apiClient.put<T>(`${path()}/${id}`, model), nullResult<T>()),
    remove: (id) =>
      requestOrFallback(() => apiClient.delete<T>(`${path()}/${id}`), nullResult<T>()),
  };
}
